import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { UploadCloud, FileText, ArrowRight, ArrowLeft } from "lucide-react";

export default function NewProject() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [projectType, setProjectType] = useState("new_construction");
  const [notes, setNotes] = useState("");
  const [pdf, setPdf] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Only PDF files are allowed");
      return;
    }
    if (f.size > 30 * 1024 * 1024) {
      toast.error("PDF too large (max 30 MB)");
      return;
    }
    setPdf(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!pdf) {
      toast.error("Please select a PDF plan set");
      return;
    }
    if (!name.trim()) {
      toast.error("Project name required");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", name);
      fd.append("address", address);
      fd.append("project_type", projectType);
      fd.append("notes", notes);
      fd.append("pdf", pdf);
      const { data: project } = await api.post("/projects", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Kick off estimate generation in background; don't await it.
      api.post(`/projects/${project.project_id}/estimate`).catch(() => {
        // backend will set status='error' if it fails — detail page surfaces it
      });
      toast.success("Plan uploaded — TAKT. is analyzing your plans.");
      navigate(`/projects/${project.project_id}`, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-3xl" data-testid="new-project-page">
        <button
          onClick={() => navigate("/dashboard")}
          className="text-sm text-slate-400 hover:text-white flex items-center gap-1 mb-6 transition-colors"
          data-testid="back-to-dashboard-btn"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to projects
        </button>

        <div className="text-[10px] tracking-[0.3em] uppercase text-[#FF6A00] font-semibold mb-2">
          New estimate
        </div>
        <h1
          className="text-3xl md:text-4xl font-black tracking-tight text-white mb-2"
          style={{ fontFamily: "Chivo, sans-serif" }}
          data-testid="new-project-heading"
        >
          Upload a plan set
        </h1>
        <p className="text-slate-400 mb-10">
          Drop a residential PDF and tell us a bit about the job. The AI does the rest.
        </p>

        <form onSubmit={handleSubmit} className="space-y-8" data-testid="new-project-form">
          {/* Upload area */}
          <div>
            <Label className="text-xs tracking-[0.2em] uppercase text-slate-400 font-semibold">
              Plan PDF
            </Label>
            <label
              htmlFor="pdf-input"
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              className={`mt-3 block border-2 border-dashed cursor-pointer p-10 text-center transition-colors ${
                dragOver
                  ? "border-[#FF6A00] bg-[#171A20]"
                  : "border-[#2A2F38] hover:border-slate-500 bg-[#171A20]/50"
              }`}
              data-testid="pdf-drop-area"
            >
              {pdf ? (
                <div className="flex flex-col items-center gap-2">
                  <FileText className="h-10 w-10 text-[#FF6A00]" />
                  <div className="text-white font-semibold" data-testid="selected-pdf-name">
                    {pdf.name}
                  </div>
                  <div className="text-xs text-slate-400">
                    {(pdf.size / 1024 / 1024).toFixed(2)} MB · Click to change
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <UploadCloud className="h-10 w-10 text-slate-500" />
                  <div className="text-white font-semibold">Drop your PDF here</div>
                  <div className="text-xs text-slate-400">or click to browse · 30 MB max</div>
                </div>
              )}
              <input
                id="pdf-input"
                type="file"
                accept="application/pdf,.pdf"
                onChange={(e) => handleFile(e.target.files?.[0])}
                className="hidden"
                data-testid="pdf-file-input"
              />
            </label>
          </div>

          {/* Project name */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="proj-name" className="text-xs tracking-[0.2em] uppercase text-slate-400 font-semibold">
                Project name
              </Label>
              <Input
                id="proj-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="mt-2 bg-[#171A20] border-[#2A2F38] text-white focus:border-[#FF6A00]"
                placeholder="123 Maple St — New build"
                data-testid="project-name-input"
              />
            </div>
            <div>
              <Label htmlFor="proj-type" className="text-xs tracking-[0.2em] uppercase text-slate-400 font-semibold">
                Project type
              </Label>
              <Select value={projectType} onValueChange={setProjectType}>
                <SelectTrigger
                  id="proj-type"
                  className="mt-2 bg-[#171A20] border-[#2A2F38] text-white"
                  data-testid="project-type-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#171A20] border-[#2A2F38] text-slate-200">
                  <SelectItem value="new_construction">New construction</SelectItem>
                  <SelectItem value="remodel">Remodel</SelectItem>
                  <SelectItem value="addition">Addition</SelectItem>
                  <SelectItem value="adu">ADU</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="address" className="text-xs tracking-[0.2em] uppercase text-slate-400 font-semibold">
              Job site address
            </Label>
            <Input
              id="address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className="mt-2 bg-[#171A20] border-[#2A2F38] text-white focus:border-[#FF6A00]"
              placeholder="123 Maple St, Anytown, CA"
              data-testid="address-input"
            />
          </div>

          <div>
            <Label htmlFor="notes" className="text-xs tracking-[0.2em] uppercase text-slate-400 font-semibold">
              Notes for the estimator (optional)
            </Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-2 bg-[#171A20] border-[#2A2F38] text-white focus:border-[#FF6A00]"
              rows={4}
              placeholder="Any specifics — finish level, custom items, exclusions, regional cost hints…"
              data-testid="notes-input"
            />
          </div>

          <div className="border-t border-[#2A2F38] pt-6 flex items-center justify-between">
            <div className="text-xs text-slate-500">
              Powered by TAKT. · Avg analysis time ~30–60s
            </div>
            <Button
              type="submit"
              disabled={submitting || !pdf}
              className="bg-[#FF6A00] hover:bg-[#E65C00] text-white rounded-md px-6 py-5"
              data-testid="submit-new-project-btn"
            >
              {submitting ? "Uploading…" : "Upload & estimate"}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </form>
      </div>
    </AppShell>
  );
}
